import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import { logo } from "@/content/site";

/* ظهور تدريجي عند التمرير */
export const Reveal = ({ children, delay = 0, y = 28, className = "", as = "div" }) => {
  const reduce = useReducedMotion();
  const M = motion[as] || motion.div;
  if (reduce) return <div className={className}>{children}</div>;
  return (
    <M
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </M>
  );
};

/* كشف سطري بقناع */
export const MaskLines = ({ lines, className = "", lineClass = "", delay = 0 }) => {
  const reduce = useReducedMotion();
  return (
    <span className={className}>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden pb-[0.08em]">
          {reduce ? (
            <span className={`block ${lineClass}`}>{line}</span>
          ) : (
            <motion.span
              className={`block ${lineClass}`}
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 1, delay: delay + i * 0.1, ease: [0.16, 1, 0.3, 1] }}
            >
              {line}
            </motion.span>
          )}
        </span>
      ))}
    </span>
  );
};

/* سهم متحرّك داخل الأزرار والروابط */
export const Arrow = ({ className = "" }) => (
  <span className={`ap-arrow ${className}`} aria-hidden="true">
    <ArrowUpRight strokeWidth={2.4} />
    <ArrowUpRight strokeWidth={2.4} />
  </span>
);

/* تسمية القسم */
export const Label = ({ children, dark = false, className = "" }) => (
  <span className={`ap-label ${dark ? "ap-label-dark" : ""} ${className}`}>
    <span className="ap-dot" />
    {children}
  </span>
);

/* ترويسة قسم */
export const SectionHead = ({ label, title, body, align = "start", dark = false, className = "" }) => {
  const center = align === "center";
  return (
    <div className={`${center ? "mx-auto max-w-3xl text-center" : "max-w-2xl"} ${className}`}>
      <Reveal>
        <Label dark={dark}>{label}</Label>
      </Reveal>
      <Reveal delay={0.08}>
        <h2
          className={`ap-display mt-5 text-[2.1rem] sm:text-[2.7rem] lg:text-[3.25rem] ${
            dark ? "text-white" : "text-[var(--text)]"
          }`}
        >
          {title}
        </h2>
      </Reveal>
      {body && (
        <Reveal delay={0.14}>
          <p
            className={`mt-5 text-[15px] leading-[1.9] sm:text-base ${center ? "mx-auto" : ""} max-w-xl ${
              dark ? "text-white/65" : "text-[var(--muted)]"
            }`}
          >
            {body}
          </p>
        </Reveal>
      )}
    </div>
  );
};

/* عنصر قائمة بعلامة صح */
export const Check = ({ children, className = "" }) => (
  <li className={`ap-check ${className}`}>
    <CheckCircle2 className="h-5 w-5" strokeWidth={2} />
    <span>{children}</span>
  </li>
);

/* شارة دائرية دوّارة بنص حول الشعار */
export const RotatingBadge = ({ text, href, className = "", testid }) => (
  <a
    href={href}
    data-testid={testid}
    className={`group relative grid h-28 w-28 place-items-center rounded-full bg-white text-[var(--text)] shadow-[0_20px_40px_-20px_rgba(21,21,21,0.35)] sm:h-32 sm:w-32 ${className}`}
    aria-label={text}
  >
    <svg viewBox="0 0 100 100" className="ap-spin absolute inset-0 h-full w-full" aria-hidden="true">
      <defs>
        <path id="ap-circle" d="M50,50 m-37,0 a37,37 0 1,1 74,0 a37,37 0 1,1 -74,0" />
      </defs>
      <text className="fill-current text-[8px] font-bold uppercase tracking-[0.2em]" direction="ltr">
        <textPath href="#ap-circle">APEX DOORS • PIVOT DOORS • APEX DOORS • PIVOT DOORS •</textPath>
      </text>
    </svg>
    <span className="grid h-14 w-14 place-items-center rounded-full bg-[var(--yellow)] transition-transform duration-700 group-hover:scale-110 sm:h-16 sm:w-16">
      <img src={logo.src} alt="" className="h-5 w-auto brightness-0 sm:h-6" />
    </span>
  </a>
);

/* رقم هاتف يُعرض دائمًا من اليسار لليمين */
export const Ltr = ({ children, className = "" }) => (
  <span dir="ltr" className={`inline-block ${className}`} style={{ unicodeBidi: "isolate" }}>
    {children}
  </span>
);

export const WhatsAppIcon = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.966 1.164-.198.199-.396.223-.693.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.76-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.347-.355.52-.554.174-.199.232-.332.347-.554.116-.223.058-.415-.016-.58-.075-.164-.667-1.603-.913-2.195-.242-.58-.487-.5-.665-.5-.173 0-.372-.025-.57-.025-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.002-5.45 4.437-9.884 9.888-9.884a9.82 9.82 0 016.988 2.898 9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.82 11.82 0 00-3.48-8.413Z" />
  </svg>
);
